import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import { Box, Button, Typography, useTheme, alpha } from '@mui/material';
import { Loader } from '../../global/index';
import useLogout from '../../hooks/useLogout';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import './home.css';
import { Report } from '../../types/reports';
import useAuth from '../../hooks/useAuth';
import { SocketContext } from '../../context/SocketProvider';
import { ISocketContext } from '../../types/socketclient';

const Home = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const [searchParams] = useSearchParams();
  const aiTrainingIncomplete =
    searchParams.get('ai_training_incomplete') === 'true';

  const theme = useTheme();
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState<Report[]>([]);
  const [flex, setFlex] = useState<boolean>();

  const { setShouldConnectSocket } = useContext(
    SocketContext,
  ) as ISocketContext;

  useEffect(() => {
    // @ts-expect-error
    document.querySelector('main').style.backgroundColor = '#0962a1';
  }, []);

  useEffect(() => {
    if (location?.state?.authenticated) setShouldConnectSocket(true);
  }, [location?.state?.authenticated, setShouldConnectSocket]);

  useEffect(() => {
    setLoading(true);
    const getUserReports = async () => {
      try {
        const response = await axiosPrivate.post('/get-user-reports');
        if (response.status === 200) {
          setReports(response.data);
          if (response.data.length < 3) {
            setFlex(true);
          }

          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
      }
    };
    getUserReports();
  }, [axiosPrivate]);

  const signOut = async () => {
    await logout();
    navigate('/');
  };

  const onButtonClicked = (report: Report) => {
    // Check if this is the VPSAI Training report with an external URL
    if (report.id === 'vpsai-training' && report.externalUrl) {
      window.open(report.externalUrl, '_blank');
      return;
    }
    // Check if this is the Find-A-Fix report that routes internally
    if (report.id === 'find-a-fix') {
      navigate('/find-a-fix');
      return;
    }
    // Check if this is the Assessment Creator
    if (report.id === 'assessment-creator') {
      navigate('/assessment-creator');
      return;
    }
    navigate(`/report/${report.id}`, { state: report });
  };

  if (loading) return <Loader />;

  const generateReports = () => {
    // Start with just the reports from the API
    const reportsList = [...reports];

    // Conditionally add VPSAI Training report at the beginning
    if (
      ['beta', 'local'].includes(process.env.REACT_APP_SERVER_ENV as string)
    ) {
      reportsList.unshift({
        id: 'vpsai-training',
        title: 'VPSAI Training',
        description: 'Access training materials for VPS AI system',
        script: '/demo5/',
        externalUrl: 'https://betavpsreports.vpstraining.com/demo5/',
      });
    }

    // Add Find-A-Fix and Assessment Creator only for beta/local environments
    if (
      ['beta', 'local'].includes(process.env.REACT_APP_SERVER_ENV as string)
    ) {
      reportsList.unshift({
        id: 'find-a-fix',
        title: 'Find-A-Fix AI',
        description:
          'AI-powered automotive diagnostic tool that helps technicians identify and fix vehicle issues through intelligent conversation and analysis',
        script: '/find-a-fix',
      });

      reportsList.unshift({
        id: 'assessment-creator',
        title: 'Assessment Creator',
        description:
          'AI-powered assessment builder that generates questions from reference materials using OpenAI',
        script: '/assessment-creator',
      });
    }

    if (!reportsList.length) return [];

    return reportsList.map((report) => {
      return (
        <Button
          variant="contained"
          onClick={() => {
            onButtonClicked(report);
          }}
          key={`${report.id}-card`}
          className="report-card"
          sx={{
            bgcolor: alpha(theme.brandcolor.primaryDark, 0.6),
            color: theme.brandcolor.primaryLight,
            textTransform: 'none',
            borderRadius: '10px',
            display: 'block',
          }}
        >
          <Typography variant="h6">{report.title}</Typography>
          <SimpleBar className="simple-bar-scroll">
            <Typography
              paragraph
              className="report-desc"
              sx={{ fontSize: 'small' }}
            >
              {report.description}
            </Typography>
          </SimpleBar>
        </Button>
      );
    });
  };

  return (
    <Box className="full-screen-container report-card-container">
      {aiTrainingIncomplete && (
        <Box
          sx={{
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.contrastText,
            padding: theme.spacing(2),
            borderRadius: '8px',
            marginBottom: theme.spacing(3),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="body1">
            Please complete your VPSAI training to unlock all features.
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              window.open(
                'https://betavpsreports.vpstraining.com/demo5/',
                '_blank',
              );
            }}
          >
            Go to Training
          </Button>
        </Box>
      )}

      <Box className="role-buttons">
        {auth.roles.includes('admin') && (
          <>
            <Box>
              <Button
                variant="contained"
                sx={{ margin: theme.spacing(3) }}
                onClick={() => {
                  navigate('/admin');
                }}
              >
                Admin Panel
              </Button>
            </Box>
            <Box>
              <Button
                variant="contained"
                sx={{ margin: theme.spacing(3) }}
                onClick={() => {
                  navigate('/admin/workspaces');
                }}
              >
                Workspaces Panel
              </Button>
            </Box>
          </>
        )}
        {auth.roles.includes('manager') && (
          <Box>
            <Button
              variant="contained"
              onClick={() => {
                navigate('/manager');
              }}
            >
              Manager Panel
            </Button>
          </Box>
        )}
      </Box>
      {['beta', 'local'].includes(process.env.REACT_APP_SERVER_ENV as string) &&
        auth.aiTrainingComplete && (
          <Box>
            <Button
              variant="contained"
              color="error"
              href="https://vpsai.vpstraining.com"
            >
              Go To VPS AI
            </Button>
          </Box>
        )}

      <Box
        id={flex ? 'flex-reports' : ''}
        className="report-card-grid"
        sx={{ gridGap: theme.spacing(4) }}
      >
        {generateReports()}
      </Box>
      <Box className="flexGrow">
        <Button variant="contained" color="primary" onClick={signOut}>
          Sign Out
        </Button>
      </Box>
    </Box>
  );
};

export default Home;
